import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import strings from "../global/strings";

export const companyQueryKey = ["company"] as const;

// Mirrors roleAccess("company")'s "company" branch in src/global/function.js —
// no "delete" key for this model, unlike EmployeePermissions.
export interface CompanyPermissions {
	edit: boolean;
	view: boolean;
	viewExtApp: boolean;
	viewIdentification: boolean;
}

// Mirrors billing_system_BE/models/preference.js
export interface Preference {
	preference_id: string;
	company_info_id: string;
	template_id?: string | null;
	tax: number;
	vat: boolean;
	discount: boolean;
	service_charge: number;
	esf: number;
	terms_condition?: string | null;
}

// Mirrors billing_system_BE/models/identification.js — read by
// identificationViewAccess-gated fields in company-setting.page.jsx
export interface Identification {
	identification_id: string;
	company_info_id: string;
	registration_type?: string | null;
	registration_number?: string | null;
	username?: string | null;
	password?: string | null;
}

// Mirrors billing_system_BE/models/company.js, plus the associations
// getCompanyById includes (billing_system_BE/controllers/company.controller.js:88-114)
export interface Company {
	company_info_id: string;
	company_id: number;
	company_name: string;
	company_code: string;
	company_type: string;
	company_province?: string | null;
	company_district?: string | null;
	company_city?: string | null;
	company_address?: string | null;
	company_country?: string | null;
	company_email: string;
	company_phone: string;
	company_mobile: string;
	company_color_code?: string | null;
	company_logo?: string | null;
	company_website?: string | null;
	company_facebook?: string | null;
	company_verified: boolean;
	company_data_corrupt: boolean;
	company_status: string;
	preference?: Preference;
	identification?: Identification;
}

// Mirrors billing_system_BE/helper/snapshotHelper.js's buildSnapshotData() —
// the exact subset of company/preference/identification fields captured into
// company_snapshots.data (JSONB) at bill-creation time. Every field is
// explicitly `?? null`-defaulted on write (never omitted), so nullability
// here is exact, not a guess. Deliberately narrower than Company/Preference/
// Identification above (no ids, no IRD username/password — the snapshot
// helper never spreads, it lists fields individually to keep credentials out).
export interface CompanySnapshotData {
	company: {
		company_name: string | null;
		company_address: string | null;
		company_logo: string | null;
		company_phone: string | null;
		company_mobile: string | null;
		company_email: string | null;
		company_color_code: string | null;
		company_type: string | null;
		company_province: string | null;
		company_district: string | null;
		company_city: string | null;
		company_country: string | null;
		company_website: string | null;
		company_facebook: string | null;
	};
	preference: {
		template_id: string | null;
		terms_condition: string | null;
		service_charge: number;
		tax: number;
		vat: boolean;
		discount: boolean;
		esf: number;
	} | null;
	identification: {
		registration_type: string | null;
		registration_number: string | null;
	} | null;
}

interface CompanyDetailsResult {
	company: Company;
	portalConnection: boolean;
}

// getCompanyById returns portalConnection as a sibling of result, not nested in it
// (billing_system_BE/controllers/company.controller.js:136-141)
async function fetchCompanyDetails(): Promise<CompanyDetailsResult> {
	const res = await axios.get(`${strings.url}company/id`, {
		withCredentials: true,
	});

	if (!res.data.success) {
		throw new Error(res.data.message);
	}

	return { company: res.data.result, portalConnection: res.data.portalConnection };
}

// Shared, cached read — every consumer (nav, dashboard, print templates,
// company-setting, etc.) calls this same hook/queryKey, so React Query dedupes
// them into a single network request regardless of how many components mount it
// at once.
export function useCompanyDetails() {
	return useQuery({
		queryKey: companyQueryKey,
		queryFn: fetchCompanyDetails,
	});
}

// Everything sent in the update request body is a subset of Company's own
// fields (the formik form is seeded from this same shape) — no separate
// payload type needed, matching customer.queries.ts's CustomerPayload pattern.
type CompanyPayload = Partial<Company>;

async function putUpdateCompany(companyData: CompanyPayload): Promise<Company> {
	const res = await axios.put(`${strings.url}company/update`, companyData, {
		withCredentials: true,
	});

	if (!res.data.success) {
		throw new Error(res.data.message);
	}

	return res.data.result;
}

export function useUpdateCompany() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: putUpdateCompany,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: companyQueryKey });
		},
	});
}

export type PreferencePayload = Partial<Preference> & {
	// company-setting.page.jsx's formik also tracks these two UI-only toggle
	// flags alongside the real Preference fields before sending the request
	serviceChargeEnabled?: boolean;
	esfEnabled?: boolean;
};

// updatePreference does not refetch the row after updating — it echoes req.body
// back as `result` (billing_system_BE/controllers/company.controller.js:217-256),
// including the client's original unsanitized terms_condition (BE sanitizes what
// it stores, not what it returns) — invalidate rather than trust this response.
async function putUpdateCompanyPreference(
	preferenceData: PreferencePayload,
): Promise<PreferencePayload> {
	const res = await axios.put(
		`${strings.url}company/update/preference`,
		preferenceData,
		{ withCredentials: true },
	);

	if (!res.data.success) {
		throw new Error(res.data.message);
	}

	return res.data.result;
}

export function useUpdateCompanyPreference() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: putUpdateCompanyPreference,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: companyQueryKey });
		},
	});
}

// --- External app connection (company-app-setting.component.tsx) ---

export const availableAppsQueryKey = ["company", "available-apps"] as const;
export const connectedAppsQueryKey = ["company", "connected-apps"] as const;

// billing_system_BE/controllers/company.controller.js's getBillMeApplicationList
// (GET company/application) — SystemKey.findAll({ attributes: ["system_type",
// "bill_model", "system_access"] }), the global registry of systems BillMe can
// connect to. system_access here is the *global* kill switch for the system
// (SystemKey.system_access) — distinct from a company's own connection-level
// access flag on ConnectedApp below.
export interface ExternalApp {
	system_type: string;
	bill_model: "A" | "B";
	system_access: boolean;
}

// Mirrors billing_system_BE/models/systemKey.js's SystemKey, as nested by
// getCompanyApplicationList's `include: [{ model: SystemKey, attributes: [...] }]`
// (belongsTo default alias singularizes "system_keys" -> "system_key"). No
// explicit `required` on that include, so Sequelize defaults to a LEFT JOIN —
// nullable in principle even though system_type should always resolve in practice.
export interface ConnectedAppSystemKey {
	system_type: string;
	system_access: boolean;
	webhook_url: string | null;
	bill_model: "A" | "B";
}

// Mirrors billing_system_BE/models/companySystemCode.js — a company's own
// connection record to one external system. system_code is AES-256 encrypted
// at rest (format ivHex:encryptedHex) and this endpoint never decrypts it —
// the "Current System Code" copy-to-clipboard action in the UI has always
// copied the encrypted value, not a plaintext secret; unchanged behavior.
export interface ConnectedApp {
	company_system_code_id: string;
	company_info_id: string;
	system_type: string;
	system_code: string;
	system_access: boolean;
	system_key: ConnectedAppSystemKey | null;
}

// system_code is deliberately absent — connectToApplication/updateApplicationConnection
// (billing_system_BE/controllers/company.controller.js) both ignore it entirely;
// the server always generates/regenerates it itself (see RegenerateAppCodePayload).
export interface ConnectApplicationPayload {
	company_info_id: string;
	system_type: string;
	system_access: boolean;
}

export interface UpdateAppConnectionPayload extends ConnectApplicationPayload {
	company_system_code_id: string;
}

async function fetchAvailableApps(): Promise<ExternalApp[]> {
	const res = await axios.get(`${strings.url}company/application`, {
		withCredentials: true,
	});

	if (!res.data.success) {
		throw new Error(res.data.message);
	}

	return res.data.result;
}

export function useAvailableApps() {
	return useQuery({
		queryKey: availableAppsQueryKey,
		queryFn: fetchAvailableApps,
	});
}

async function fetchConnectedApps(): Promise<ConnectedApp[]> {
	const res = await axios.get(`${strings.url}company/application/list`, {
		withCredentials: true,
	});

	if (!res.data.success) {
		throw new Error(res.data.message);
	}

	return res.data.result;
}

export function useConnectedApps() {
	return useQuery({
		queryKey: connectedAppsQueryKey,
		queryFn: fetchConnectedApps,
	});
}

// Single source of truth for "is an external app connected right now, and
// under which billing model" — at most one connection is ever active (see
// company-app-setting.component.tsx's one-at-a-time guard), so callers that
// just need to gate a feature (manual bill creation for Model A, sync badges
// for either model) can use this instead of re-deriving activeConnections.
export function useActiveExternalApp() {
	const { data: connectedApps, isLoading } = useConnectedApps();
	const active = (connectedApps ?? []).find((app) => app.system_access) ?? null;
	return {
		billModel: active?.system_key?.bill_model ?? null,
		systemType: active?.system_type ?? null,
		isLoading,
	};
}

// connectToApplication/updateApplicationConnection (BE) both delegate straight
// to getCompanyById to build their response, so they return the exact same
// { result, portalConnection } shape as company/id — write straight into the
// shared companyQueryKey cache rather than just invalidating, same as the
// other company mutations above.
async function postConnectApplication(
	payload: ConnectApplicationPayload,
): Promise<CompanyDetailsResult> {
	const res = await axios.post(
		`${strings.url}company/connect/application`,
		payload,
		{ withCredentials: true },
	);

	if (!res.data.success) {
		throw new Error(res.data.message);
	}

	return { company: res.data.result, portalConnection: res.data.portalConnection };
}

export function useConnectApplication() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: postConnectApplication,
		onSuccess: (data) => {
			queryClient.setQueryData(companyQueryKey, data);
			queryClient.invalidateQueries({ queryKey: connectedAppsQueryKey });
		},
	});
}

async function putUpdateAppConnection(
	payload: UpdateAppConnectionPayload,
): Promise<CompanyDetailsResult> {
	const res = await axios.put(
		`${strings.url}company/update/application`,
		payload,
		{ withCredentials: true },
	);

	if (!res.data.success) {
		throw new Error(res.data.message);
	}

	return { company: res.data.result, portalConnection: res.data.portalConnection };
}

export function useUpdateAppConnection() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: putUpdateAppConnection,
		onSuccess: (data) => {
			queryClient.setQueryData(companyQueryKey, data);
			queryClient.invalidateQueries({ queryKey: connectedAppsQueryKey });
		},
	});
}

export interface RegenerateAppCodePayload {
	system_type: string;
}

interface RegenerateAppCodeResult {
	system_code: string;
}

// regenerateSystemCode (billing_system_BE/controllers/company.controller.js) returns
// { success, message, system_code } directly — not the { result, portalConnection }
// envelope the other application-connection endpoints share.
async function postRegenerateAppCode(
	payload: RegenerateAppCodePayload,
): Promise<RegenerateAppCodeResult> {
	const res = await axios.post(
		`${strings.url}company/regenerate/application-code`,
		payload,
		{ withCredentials: true },
	);

	if (!res.data.success) {
		throw new Error(res.data.message);
	}

	return { system_code: res.data.system_code };
}

export function useRegenerateAppCode() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: postRegenerateAppCode,
		onSuccess: (data, variables) => {
			// Patch just the one row rather than invalidating — cheaper than a
			// full refetch and avoids a flash while the list reloads.
			queryClient.setQueryData<ConnectedApp[]>(connectedAppsQueryKey, (old) =>
				old?.map((app) =>
					app.system_type === variables.system_type
						? { ...app, system_code: data.system_code }
						: app,
				),
			);
		},
	});
}
